import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, X, Sparkles } from 'lucide-react';

/**
 * Onboarding checklist (Jaime GO 2026-10-01): dismissible card on the main
 * dashboard for brand-new users. 4 steps in Spanish, each linking to the right
 * route. Completion persists in localStorage (key: vecindus.onboarding.v1);
 * auto-hides once every step is done or the user dismisses it. Real actions
 * also complete steps: pages dispatch window event 'vecindus:onboarding-done'
 * with the step id (e.g. HomeTasks dispatches 'task' after a real create).
 */
const STORAGE_KEY = 'vecindus.onboarding.v1';

interface OnboardingState { dismissed: boolean; done: string[] }

const STEPS = [
  { id: 'invite', label: 'Invita a tu condominio', desc: 'Agrega a tu familia o vecinos', to: '/building/invites' },
  { id: 'task', label: 'Crea tu primera tarea', desc: 'Organiza tu hogar', to: '/tasks' },
  { id: 'qr', label: 'Comparte tu QR de acceso', desc: 'Para tus visitantes', to: '/my-qr' },
  { id: 'budget', label: 'Explora tu estado de cuenta', desc: 'Pagos y saldos', to: '/payments' },
];

function load(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { dismissed: Boolean(parsed.dismissed), done: Array.isArray(parsed.done) ? parsed.done : [] };
    }
  } catch { /* corrupted state → fresh start */ }
  return { dismissed: false, done: [] };
}

export default function OnboardingChecklist() {
  const [state, setState] = useState<OnboardingState>(() => load());
  const hidden = state.dismissed || STEPS.every((s) => state.done.includes(s.id));

  useEffect(() => {
    const onDone = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (!id) return;
      setState((prev) => {
        if (prev.done.includes(id)) return prev;
        const next = { ...prev, done: [...prev.done, id] };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    };
    window.addEventListener('vecindus:onboarding-done', onDone);
    return () => window.removeEventListener('vecindus:onboarding-done', onDone);
  }, []);

  const markDone = (id: string) => {
    setState((prev) => {
      if (prev.done.includes(id)) return prev;
      const next = { ...prev, done: [...prev.done, id] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const dismiss = () => {
    const next = { ...state, dismissed: true };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setState(next);
  };

  if (hidden) return null;

  return (
    <div data-testid="onboarding-checklist" className="mb-6 rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-5 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#36CCD8]" />
          <h2 className="text-lg font-bold text-[var(--v-text-primary)]">Primeros pasos</h2>
          <span className="rounded-full bg-[#36CCD8]/10 px-2 py-0.5 text-xs font-semibold text-[#36CCD8]">
            {state.done.length}/{STEPS.length}
          </span>
        </div>
        <button onClick={dismiss} aria-label="Descartar" className="rounded-lg p-1.5 text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {STEPS.map((step) => {
          const done = state.done.includes(step.id);
          return (
            <li key={step.id}>
              <Link
                to={step.to}
                onClick={() => markDone(step.id)}
                className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${done ? 'border-[#36CCD8]/40 bg-[#36CCD8]/5 opacity-70' : 'border-[var(--v-border-light)] hover:bg-[var(--v-surface-2)]'}`}
              >
                {done
                  ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#36CCD8]" />
                  : <span className="h-5 w-5 flex-shrink-0 rounded-full border-2 border-[var(--v-border-light)]" />}
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-semibold ${done ? 'line-through text-[var(--v-text-secondary)]' : 'text-[var(--v-text-primary)]'}`}>{step.label}</span>
                  <span className="block truncate text-xs text-[var(--v-text-secondary)]">{step.desc}</span>
                </span>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-[var(--v-text-secondary)]" />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
